import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

const Wrap = ({ eyebrow, title, children }) => (
  <div className="bg-alabaster pt-28 lg:pt-36">
    <div className="mx-auto max-w-3xl px-4 pb-24 sm:px-6">
      <Reveal className="border-b border-linec pb-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-crimson">{eyebrow}</p>
        <h1 className="mt-4 font-serif text-5xl font-light">{title}</h1>
      </Reveal>
      <div className="prose-justify space-y-6 pt-10 text-sm leading-relaxed text-mutedc">{children}</div>
    </div>
  </div>
);

const SIZE_ROWS = [
  ["M (38)", 35, 37, 38, 36, 40],
  ["L (40)", 35, 39, 40, 38, 42],
  ["XL (42)", 35, 39, 42, 40, 44],
  ["XXL (44)", 35, 39, 44, 42, 46],
  ["3XL (46)", 35, 39, 46, 44, 48],
  ["4XL (48)", 35, 39, 48, 46, 50],
  ["5XL (50)", 36, 39, 50, 48, 52],
  ["6XL (52)", 36, 39, 52, 50, 54],
];

export function SizeGuide() {
  useSEO({ title: "Size Guide — Just Kords", description: "Find your perfect fit in Just Kords co-ord sets. Detailed bust, waist and hip measurements with IN/US/UK/EU size conversion." });
  return (
    <Wrap eyebrow="Fit & Sizing" title="Size Guide">
      <p>Our size numbers correspond to to-fit bust in inches (for example, M = 38). Our sets are cut for a relaxed, true-to-size fit. Between sizes? Size up for a drapier fall.</p>
      <div className="overflow-x-auto">
        <p>All measurements in inches (garment flat measurements). Our size numbers correspond to to-fit bust (M = 38). Between sizes? Size up for a drapier fall.</p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse text-sm" data-testid="static-size-table">
          <thead>
            <tr className="border-b border-ink/20 font-mono text-[10px] uppercase tracking-widest text-mutedc">
              <th className="py-3 text-left">Size</th>
              <th className="py-3 text-right">Top Length</th>
              <th className="py-3 text-right">Bottom Length</th>
              <th className="py-3 text-right">Bust</th>
              <th className="py-3 text-right">Waist</th>
              <th className="py-3 text-right">Hips</th>
            </tr>
          </thead>
          <tbody>
            {SIZE_ROWS.map((r) => (
              <tr key={r[0]} className="border-b border-linec">
                <td className="py-2.5 font-semibold">{r[0]}</td>
                <td className="py-2.5 text-right font-mono text-xs">{r[1]}"</td>
                <td className="py-2.5 text-right font-mono text-xs">{r[2]}"</td>
                <td className="py-2.5 text-right font-mono text-xs">{r[3]}"</td>
                <td className="py-2.5 text-right font-mono text-xs">{r[4]}"</td>
                <td className="py-2.5 text-right font-mono text-xs">{r[5]}"</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="font-mono text-[10px] uppercase tracking-widest">Pls note: a variation of +/- 1 inch may occur due to fabric viscosity and is not considered as defective or wrong size.</p>
      </div>
      <p>Unsure about your size? WhatsApp us your measurements and the set you're eyeing — we'll recommend the best fit within hours.</p>
      <Link to="/contact" data-testid="size-guide-contact-cta" className="inline-block rounded-full bg-crimson px-7 py-3 text-xs font-semibold text-white">Ask on WhatsApp</Link>
    </Wrap>
  );
}

export function ShippingReturns() {
  useSEO({ title: "Shipping & Exchange Policy — Just Kords", description: "Shipping timelines and exchange policy for Just Kords co-ord sets — dispatch in 24-48 hours, free India shipping above ₹2,999. Exchange only for manufacturing defects or wrong size received, with uncut parcel-opening video within 48 hours." });
  return (
    <Wrap eyebrow="Policy" title="Shipping & Returns">
      <h3 className="pt-2 font-serif text-2xl text-ink">Shipping</h3>
      <ul className="list-disc space-y-2 pl-5">
        <li>Orders dispatch within 24–48 hours, Monday to Saturday.</li>
        <li><strong className="text-ink">Free standard shipping</strong> across India on orders above ₹2,999; ₹99 below that.</li>
        <li>Express delivery (2–3 days) available at ₹199 at checkout.</li>
        <li>Every order ships with a tracking ID — follow it any time on our <Link className="text-crimson underline" to="/track-order">Track Order</Link> page.</li>
        <li>International shipping to select countries on request via WhatsApp.</li>
      </ul>
      <h3 className="pt-4 font-serif text-2xl text-ink">Returns & Exchanges</h3>
      <ul className="list-disc space-y-2 pl-5">
        <li>Returns are <strong className="text-ink">not accepted</strong>. "Exchange only" is accepted for manufacturing defects or the wrong size received.</li>
        <li>An <strong className="text-ink">uncut, unedited, continuous parcel-opening video</strong> is mandatory for any exchange claim.</li>
        <li>Please get in touch with us within <strong className="text-ink">48 hours of receiving the parcel</strong> on our WhatsApp (+91 99607 05886) or email (justkords@gmail.com) to register your claim.</li>
      </ul>
      <p>Claims registered without the unboxing video or after 48 hours of delivery cannot be accepted. For anything else, message us with your Order ID and we'll help however we can.</p>
    </Wrap>
  );
}

export function PrivacyPolicy() {
  useSEO({ title: "Privacy Policy — Just Kords", description: "How Just Kords collects, uses and protects your personal data." });
  return (
    <Wrap eyebrow="Legal" title="Privacy Policy">
      <p>Just Kords ("we", "our"), operating justkords.com, respects your privacy. This policy explains what we collect and why.</p>
      <h3 className="pt-2 font-serif text-2xl text-ink">What we collect</h3>
      <p>Contact details (name, email, phone), delivery addresses, order history, and — where you choose to pay online — payment confirmations handled by Razorpay. We never see or store your full card details; all online payments are processed on Razorpay's PCI-DSS compliant infrastructure.</p>
      <h3 className="pt-2 font-serif text-2xl text-ink">How we use it</h3>
      <p>To fulfil orders, provide support over WhatsApp and email, send order updates, and — only with your consent — share new drops and offers through our newsletter. You can unsubscribe at any time.</p>
      <h3 className="pt-2 font-serif text-2xl text-ink">Your rights</h3>
      <p>Write to justkords@gmail.com to access, correct or delete your data. We retain order records only as long as legally required.</p>
      <p className="font-mono text-[10px] uppercase tracking-widest">Last updated: 2025</p>
    </Wrap>
  );
}

export function Terms() {
  useSEO({ title: "Terms of Service — Just Kords", description: "Terms governing the use of justkords.com and purchases of Just Kords co-ord sets." });
  return (
    <Wrap eyebrow="Legal" title="Terms of Service">
      <p>Welcome to justkords.com. By using this site or placing an order, you agree to these terms.</p>
      <h3 className="pt-2 font-serif text-2xl text-ink">Orders & pricing</h3>
      <p>All prices are in Indian Rupees (INR) unless stated otherwise; USD figures are indicative conversions. We may cancel and fully refund an order in case of pricing errors or stock unavailability. Placing an order constitutes an offer to purchase; it is accepted when we confirm dispatch.</p>
      <h3 className="pt-2 font-serif text-2xl text-ink">Payments</h3>
      <p>Online payments are processed securely by Razorpay (UPI, cards, NetBanking, wallets). Applicable taxes are included in listed prices.</p>
      <h3 className="pt-2 font-serif text-2xl text-ink">Cashback wallet</h3>
      <p>Signed-in customers earn 10% cashback on the product value of every purchase (excluding shipping), credited to their Just Kords wallet once payment is confirmed. Cashback is valid for 90 days from the date it is earned, is calculated on the basic (pre-discount) amount, is non-transferable and cannot be exchanged for cash. It can only be used toward future purchases on justkords.com. Cashback linked to a cancelled or refunded order may be voided or reversed.</p>
      <h3 className="pt-2 font-serif text-2xl text-ink">Intellectual property</h3>
      <p>All content on this site — the Just Kords name, wordmark, imagery and copy — belongs to Just Kords and may not be reproduced without written permission.</p>
      <h3 className="pt-2 font-serif text-2xl text-ink">Governing law</h3>
      <p>These terms are governed by the laws of India; courts of Mumbai have exclusive jurisdiction.</p>
      <p className="font-mono text-[10px] uppercase tracking-widest">Last updated: 2025</p>
    </Wrap>
  );
}

export function NotFound() {
  useSEO({ title: "Page Not Found — Just Kords" });
  return (
    <div className="flex min-h-[80vh] flex-col items-center justify-center gap-5 bg-alabaster px-4 pt-28 text-center" data-testid="not-found">
      <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-crimson">404</p>
      <h1 className="font-serif text-5xl font-light">This page went out of style.</h1>
      <p className="text-sm text-mutedc">The link may be old, or the page may have moved.</p>
      <Link to="/" data-testid="not-found-home-cta" className="mt-2 rounded-full bg-crimson px-8 py-3.5 text-sm font-semibold text-white">Back to Just Kords</Link>
    </div>
  );
}
